import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { getAntiforgeryToken, portalFetch } from "../api/http";
import { missingText } from "../i18n/texts";
import { isMigrated } from "../shell/routes";
import { SectionMessage } from "../ui/feedback";

// Piezas comunes de Configuración del Chat, Bandejas y Canales (T29–T31): textos por pantalla, lecturas y escrituras de
// /api/portal/config (los códigos HTTP son los de la página del portal actual), el diálogo de confirmación de
// Bheli.Web.Feedback y las pestañas de Shared/Tabs.razor, con el mismo HTML.

export type Label = (key: string) => string;

/** Textos de la pantalla: solo los prefijos que lee el original; una clave que falte se ve igual (⟦CLAVE⟧). */
export function usePageTexts(locale: string, name: string, prefixes: string[]): Label | null {
  const texts = useQuery({
    queryKey: ["texts", locale, `config-${name}`],
    queryFn: () =>
      portalFetch<Record<string, string>>(
        `/api/portal/texts/${locale}?prefixes=${encodeURIComponent(prefixes.join(","))}`,
      ),
  });
  const data = texts.data;
  const L = useCallback((key: string) => data?.[key] ?? missingText(key), [data]);
  return data ? L : null;
}

/** Respuesta con su código: la vista de «sin permiso» o «no existe» también trae cuerpo. */
export type Reply<T> = { status: number; body: T };

async function send<T>(url: string, init: RequestInit): Promise<Reply<T>> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  const token = getAntiforgeryToken();
  if (init.method === "POST") {
    headers.set("Content-Type", "application/json");
    if (token) headers.set("X-CSRF-TOKEN", token);
  }
  const response = await fetch(url, { ...init, headers, credentials: "same-origin" });
  const text = await response.text();
  let body: unknown = undefined;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  return { status: response.status, body: body as T };
}

/** Lectura de una vista de configuración (200, 403 o 404 con su cuerpo). */
export function readView<T>(url: string): Promise<Reply<T>> {
  return send<T>(url, { method: "GET" });
}

/** Escritura con antiforgery; el cuerpo de un rechazo trae la clave del texto que muestra el original. */
export function postJson<T>(url: string, body: unknown = {}): Promise<Reply<T>> {
  return send<T>(url, { method: "POST", body: JSON.stringify(body) });
}

/** Resultado de una acción: guardado o la clave del texto del rechazo (como ApplyAsync del original). */
export type ActionResult = {
  saved?: boolean;
  errorKey?: string;
  nameError?: boolean;
  statusConflict?: boolean;
  targetError?: boolean;
  inboxId?: number;
  forbidden?: boolean;
};

/**
 * Lo que deja el POST clásico del portal actual (navegación mejorada de Blazor): la página se vuelve a dibujar, el control
 * activo pierde el foco (FocusOnNavigate) y feedback.js reconcilia los diálogos de confirmación con «enhancedload».
 */
export function afterPost(): void {
  const heading = document.querySelector<HTMLElement>("h1");
  heading?.focus({ preventScroll: true });
  if (document.activeElement !== heading) (document.activeElement as HTMLElement | null)?.blur();
  document.dispatchEvent(new Event("enhancedload"));
}

/**
 * Enlaces a pantallas ya migradas dentro de la zona: se abren sin recargar (como la navegación mejorada de Blazor), con
 * el scroll arriba. Los demás destinos y los anclajes de la misma página siguen su curso normal.
 */
export function useSpaLinks(): void {
  const navigate = useNavigate();
  useEffect(() => {
    // El contenido de la pantalla vive en el <main> del shell (sin envoltorios propios que cambien el HTML).
    const element = document.getElementById("main");
    if (!element) return;
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || (anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || !isMigrated(url.pathname)) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      event.preventDefault();
      navigate(url.pathname + url.search + url.hash);
      window.scrollTo(0, 0);
    };
    element.addEventListener("click", onClick);
    return () => element.removeEventListener("click", onClick);
  }, [navigate]);
}

/**
 * Las pantallas Static SSR del portal actual cargan con el foco en su h1 (tabindex="-1", como FocusOnNavigate de Blazor):
 * Tab sigue desde el título. Aquí se hace lo mismo en cuanto la pantalla dibuja su contenido, también al navegar.
 */
export function useHeadingFocus(ready: boolean, page?: string): void {
  const location = useLocation();
  // `page` dice qué cuenta como otra página: abrir o cerrar una ventana por la URL (?add=true) no lo es, y el foco
  // vuelve al botón que la abrió (C30) en vez de ir al título.
  const key = page ?? location.key;
  useEffect(() => {
    if (!ready) return;
    const heading = document.querySelector<HTMLElement>("#main h1");
    if (!heading) return;
    heading.setAttribute("tabindex", "-1");
    heading.focus({ preventScroll: true });
  }, [ready, key]);
}

/** PageTitle del original. */
export function useDocumentTitle(title: string | null): void {
  useEffect(() => {
    if (title !== null) document.title = title;
  }, [title]);
}

/**
 * EditForm con Enhance del portal actual: el mismo <form method="post"> con su campo _handler (el token antiforgery va en
 * la cabecera de la escritura, no en el formulario). El envío lo hace la SPA y nunca recarga la página.
 */
export function PostForm({
  name,
  className,
  onSubmit,
  children,
}: {
  name: string;
  className?: string;
  onSubmit: (form: HTMLFormElement) => void;
  children: ReactNode;
}) {
  return (
    <form
      method="post"
      action={window.location.pathname + window.location.search}
      data-enhance=""
      className={className}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(event.currentTarget);
      }}
    >
      <input type="hidden" name="_handler" value={name} />
      <input type="hidden" name="__RequestVerificationToken" value={getAntiforgeryToken() ?? ""} />
      {children}
    </form>
  );
}

/** Tabs.razor: vistas hermanas de la misma entidad, con su URL. */
export function Tabs({ items }: { items: { label: string; href: string; active: boolean }[] }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((tab) => (
        <a
          key={tab.href}
          className={`button tab ${tab.active ? "" : "secondary"}`}
          href={tab.href}
          role="tab"
          aria-selected={tab.active ? "true" : "false"}
          aria-current={tab.active ? "page" : undefined}
        >
          {tab.label}
        </a>
      ))}
    </div>
  );
}

/**
 * ConfirmationDialog.razor (Bheli.Web.Feedback): disparador y diálogo con el mismo HTML. Abrir, cerrar, atrapar el foco y
 * mantenerlo abierto ante un rechazo lo hace feedback.js del portal actual sobre estos mismos atributos.
 */
export function ConfirmationDialog({
  id,
  triggerLabel,
  title,
  description,
  cancelLabel,
  error,
  keepOpen,
  children,
}: {
  id: string;
  triggerLabel: string;
  title: string;
  description: string;
  cancelLabel: string;
  error?: string | null;
  keepOpen?: boolean;
  children: ReactNode;
}) {
  const page = window.location.href.split("#")[0];
  return (
    <>
      <a id={`${id}-trigger`} href={`${page}#${id}`} className="button secondary" data-confirm-open={id}>
        {triggerLabel}
      </a>{" "}
      <dialog
        id={id}
        open
        className="confirmation-dialog"
        role="dialog"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        data-confirm-dialog=""
        data-keep-open={keepOpen ? "true" : "false"}
        data-confirm-completion="0"
      >
        <h2 id={`${id}-title`}>{title}</h2> <p id={`${id}-description`}>{description}</p>
        <SectionMessage message={error} tone="error" />
        <div className="confirmation-actions">
          <a href={`${page}#${id}-trigger`} className="button secondary confirmation-cancel" data-confirm-close="">
            {cancelLabel}
          </a>{" "}
          {children}
        </div>
      </dialog>
    </>
  );
}
